/**
 * Utility functions for filtering internal tracking phrases from transcript text.
 *
 * Ensures UI transcripts match what users hear in TTS by removing internal agent guidance
 * like "Now asking Q3 of 10" or "Q5 complete. Moving to Q6".
 *
 * Fixes common artifacts like double periods ("..") after filtering.
 */

/**
 * Core function: removes internal tracking phrases and cleans up resulting text.
 */
/**
 * Improved version: preserves trailing punctuation after removing tracking phrases
 */
export function filterTrackingPhrases(text: string, options?: { debug?: boolean }): string {
  if (!text || typeof text !== 'string') {
    return text ?? '';
  }

  let result = text;
  const removed: string[] = [];

  const patterns = [
    { regex: /Now asking Q\d+ of \d+\.?\s*/gi, desc: 'Now asking QX of Y' }, // Made \.? optional and \s* after
    { regex: /Q\d+ complete,\s*moving to Q\d+ of \d+\.?\s*/gi, desc: 'QX complete, moving to QY of Z' },
    { regex: /Q\d+ complete,\s*moving to closing\.?\s*/gi, desc: 'QX complete, moving to closing' },
    {
      regex: /Q\d+ complete\.\s*(?:Next is|Moving to)\s*Q\d+\.?\s*/gi,
      desc: 'Legacy: QX complete. Next is/Moving to QY',
    },
    { regex: /Now asking Q\d+ of \d+:?\s*/gi, desc: 'Now asking QX of Y:' },
    { regex: /Internal (?:tracking|verification):.*?(?=\n|$)/gi, desc: 'Internal tracking/verification' },
  ];

  // Step 1: Replace tracking phrases with a single space, but capture trailing punctuation
  for (const { regex, desc } of patterns) {
    result = result.replace(regex, (match) => {
      if (options?.debug) {
        removed.push(match.trim());
      }
      return ' '; // Always replace with one space to separate sentences
    });
  }

  // Step 2: Comprehensive but CAREFUL cleanup
  result = result
    // Normalize multiple spaces/newlines first
    .replace(/\s+/g, ' ')

    // Fix duplicate punctuation: .. → ., !!! → !, ?? → ?
    .replace(/([.!?])\1{1,}/g, '$1')

    // Remove spaced-out punctuation like ". ." or "! ?" → keep only first
    .replace(/([.!?])\s+([.!?])/g, '$1')

    // Remove ", ." → "."
    .replace(/,\s*\./g, '.')

    // DO NOT aggressively remove spaces before periods anymore
    // Removed: .replace(/\s+\./g, '.') ← this was the main culprit!

    // Ensure exactly one space after sentence-ending punctuation (., !, ?)
    // But only if followed by a word character (to avoid messing with abbreviations)
    .replace(/([.!?])\s*([A-Za-z])/g, '$1 $2')

    // Clean up any leading punctuation after cleanup (e.g., if tracking left ": ")
    .replace(/^\s*[:.]\s*/, '')
    .replace(/\s*[:.]\s*$/, ' ')

    // Trim and remove empty lines
    .trim()
    .replace(/\n\s*\n/g, '\n')
    .replace(/\s+$/gm, '');

  if (options?.debug && removed.length > 0) {
    console.log('Tracking phrases removed:', removed);
  }

  return result;
}

/**
 * Filters a single message object immutably.
 */
export function filterMessageTrackingPhrases<T extends { message: string }>(
  message: T,
  options?: { debug?: boolean }
): T {
  return {
    ...message,
    message: filterTrackingPhrases(message.message, options),
  };
}

/**
 * Filters an array of messages (only removes tracking phrases).
 */
export function filterConversationTrackingPhrases<T extends { message: string }>(
  messages: T[],
  options?: { debug?: boolean }
): T[] {
  return messages.map((msg) => filterMessageTrackingPhrases(msg, options));
}

/**
 * Checks if a message contains only tracking phrases (after filtering, nothing meaningful remains).
 */
export function isOnlyTrackingPhrases(text: string): boolean {
  if (!text || typeof text !== 'string') {
    return true;
  }

  const filtered = filterTrackingPhrases(text);
  const trimmed = filtered.trim();

  // Consider it "only tracking" if result is empty or just punctuation/short noise
  return trimmed.length === 0 || /^[!?.]*$/.test(trimmed);
}

/**
 * Fully cleans a conversation:
 * - Removes tracking phrases from all messages
 * - Completely hides messages that were purely internal tracking
 */
export function filterAndCleanConversation<T extends { message: string }>(
  messages: T[],
  options?: { debug?: boolean }
): T[] {
  return messages
    .filter((message) => !isOnlyTrackingPhrases(message.message))
    .map((msg) => filterMessageTrackingPhrases(msg, options));
}

/**
 * Optional: Utility to preview what gets removed (useful for debugging new patterns)
 */
export function debugFilterTrackingPhrases(text: string): { original: string; filtered: string; removed: string[] } {
  const removed: string[] = [];
  const filtered = filterTrackingPhrases(text, {
    debug: true,
    // Override console.log to capture
    // @ts-ignore - we're capturing internally
    // eslint-disable-next-line @typescript-eslint/no-empty-function
  }).replace(/console\.log.*\n?/g, ''); // suppress log

  // Re-run with capture
  let temp = text;
  const patterns = [
    /Now asking Q\d+ of \d+/gi,
    /Q\d+ complete, moving to Q\d+ of \d+/gi,
    /Q\d+ complete, moving to closing/gi,
    /Q\d+ complete\.\s*(?:Next is|Moving to)\s*Q\d+\.?\s*/gi,
    /Now asking Q\d+ of \d+:?\s*/gi,
    /Internal (?:tracking|verification):.*?(?=\n|$)/gi,
  ];

  for (const regex of patterns) {
    temp.replace(regex, (match) => {
      removed.push(match.trim());
      return ' ';
    });
  }

  return {
    original: text,
    filtered: filterTrackingPhrases(text),
    removed: [...new Set(removed)], // dedupe
  };
}